import { useEffect, useRef, useState } from "react";
import { Globe, Sun, Moon, ChevronDown, Check } from "lucide-react";
import { useT } from "@/i18n/LanguageContext";
import useTheme from "@/hooks/useTheme";
import useEventTracking from "@/hooks/useEventTracking";

/**
 * Compact language + theme controls used in the Header (desktop + mobile).
 */
export default function TopControls({ compact = false }) {
  const { lang, setLang, languages, t } = useT();
  const { theme, toggle } = useTheme();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const trackEvent = useEventTracking();

  useEffect(() => {
    const onClickOut = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOut);
    return () => document.removeEventListener("mousedown", onClickOut);
  }, []);

  const currentLang = languages.find((l) => l.code === lang) || languages[0];

  return (
    <div className={`flex items-center ${compact ? "gap-1.5" : "gap-2"}`}>
      {/* Language selector */}
      <div className="relative" ref={wrapRef}>
        <button
          type="button"
          data-testid="lang-toggle"
          onClick={() => setOpen((v) => !v)}
          aria-label={t.controls.language}
          className="h-9 sm:h-10 px-2.5 sm:px-3 rounded-full border border-black/10 dark:border-white/15 bg-white/70 dark:bg-white/5 backdrop-blur hover:border-[#1E63FF] transition-colors inline-flex items-center gap-1.5 text-xs font-semibold text-[#0A0A0A] dark:text-white"
        >
          <Globe className="w-3.5 h-3.5 opacity-70" strokeWidth={2} />
          <span className="hidden sm:inline uppercase tracking-wider">{currentLang.code}</span>
          <span className="sm:hidden text-sm leading-none">{currentLang.flag}</span>
          <ChevronDown className={`w-3 h-3 opacity-70 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>

        {open && (
          <div
            data-testid="lang-menu"
            className="absolute top-full mt-2 right-0 min-w-[180px] rounded-xl bg-white dark:bg-[#141414] border border-black/10 dark:border-white/10 shadow-[0_20px_50px_-15px_rgba(0,0,0,0.35)] overflow-hidden z-50"
          >
            {languages.map((l) => (
              <button
                key={l.code}
                data-testid={`lang-option-${l.code}`}
                type="button"
                onClick={() => { setLang(l.code); setOpen(false); trackEvent("language_change", l.code)(); }}
                className={`w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm text-left hover:bg-[#F5F5F7] dark:hover:bg-white/5 transition-colors ${
                  l.code === lang ? "text-[#1E63FF] font-semibold" : "text-[#0A0A0A] dark:text-white"
                }`}
              >
                <span className="text-base leading-none">{l.flag}</span>
                <span className="flex-1">{l.nativeLabel}</span>
                {l.code === lang && <Check className="w-3.5 h-3.5" />}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Theme toggle */}
      <button
        type="button"
        data-testid="theme-toggle"
        onClick={() => { toggle(); trackEvent("theme_change", theme === "dark" ? "to-light" : "to-dark")(); }}
        aria-label={t.controls.theme}
        className="h-9 sm:h-10 w-9 sm:w-10 rounded-full border border-black/10 dark:border-white/15 bg-white/70 dark:bg-white/5 backdrop-blur hover:border-[#1E63FF] transition-colors inline-flex items-center justify-center text-[#0A0A0A] dark:text-white"
      >
        {theme === "dark" ? (
          <Sun className="w-4 h-4" strokeWidth={2} />
        ) : (
          <Moon className="w-4 h-4" strokeWidth={2} />
        )}
      </button>
    </div>
  );
}
